import { Box, Button, Container, MenuItem, Select, TextField, Typography } from '@mui/material';
import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { styled } from '@mui/material/styles';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useGetAllCitiesMutation } from '../store/api/citiesApi';
import { DatePicker, LocalizationProvider } from '@mui/lab';
import {
  useSaveOfferDetailMutation,
  useUpdateOfferDetailMutation,
  useGetAllOfferDetailMutation,
} from '../store/api/offerApi';
import { useGetEnumDataMutation } from '../store/api/enumApi';
import { getSpecificEnumData, setEnumData } from '../store/state/enumSlice';
import Loading from './Loading';
import moment from 'moment';
import { fDateTimeSuffix } from '../utils/formatTime';

type Props = {};

const OfferSchema = Yup.object().shape({
  offerFor: Yup.string().required('This field is required'),
  offerTitle: Yup.string()
    .required('This field is required')
    .matches(/^[a-zA-Z\s.-]+$/, { message: 'Name must be alphabetic' })
    .trim(),
  selectedCity: Yup.string().required('This field is required'),
  offerType: Yup.string().required('This field is required'),
  offerStartsFrom: Yup.string().required('This field is required').typeError('Please enter date'),
  offerEndsAt: Yup.string().required('This field is required').typeError('Please enter date'),
  offerValue: Yup.string()
    .required('This field is required')
    .matches(/^[0-9]+(\.?[0-9]+)?$/, { message: 'Invalid number' })
    .trim(),
  promoCode: Yup.string().required('This field is required'),
});

const OfferTypeSchema = Yup.object().shape({
  offerFor: Yup.string().required('This field is required'),
  offerTitle: Yup.string()
    .required('This field is required')
    .matches(/^[a-zA-Z\s.-]+$/, { message: 'Name must be alphabetic' })
    .trim(),
  offerType: Yup.string().required('This field is required'),
  offerStartsFrom: Yup.string().required('This field is required').typeError('Please enter date'),
  offerEndsAt: Yup.string().required('This field is required').typeError('Please enter date'),
  offerValue: Yup.string()
    .required('This field is required')
    .matches(/^[0-9]+(\.?[0-9]+)?$/, { message: 'Invalid number' })
    .trim(),
  promoCode: Yup.string().required('This field is required'),
});

const ResponsiveBox = styled('div')(({ theme }) => ({
  padding: theme.spacing(1),
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
  },
}));

const FormikBox = styled('div')(({ theme }) => ({
  padding: theme.spacing(1),
  [theme.breakpoints.down('md')]: {
    marginLeft: '0px',
  },
  [theme.breakpoints.up('md')]: {
    marginLeft: '20px',
  },
}));

const AddOrUpdateOffers = (props: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [initialValues, setInitialValues] = useState({
    offerFor: '',
    offerTitle: '',
    offerType: '',
    selectedCity: '',
    offerStartsFrom: '',
    offerEndsAt: '',
    offerValue: '',
    promoCode: '',
  });
  const { state }: any = useLocation();
  const token = JSON.parse(localStorage.getItem('login') || '{}').token;
  const [saveOfferDetail, saveOfferDetailResponse] = useSaveOfferDetailMutation();
  const [updateOfferDetail, updateOfferDetailResponse] = useUpdateOfferDetailMutation();
  const [getAllCities, getAllCitiesResponse] = useGetAllCitiesMutation();
  const [getAllOfferDetail, getAllOfferDetailResponse] = useGetAllOfferDetailMutation();
  const [getEnumData, enumResponse] = useGetEnumDataMutation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [order, setOrder] = useState('desc');
  const [cityData, setCityData] = useState<any | null>();
  const [offerFor, setOfferFor] = useState([]);
  const [offerType, setOfferType] = useState([]);
  const [offerCode, setOfferCode] = useState('');

  useEffect(() => {
    getEnumData({ token: token });
  }, []);

  useEffect(() => {
    if (enumResponse.isSuccess) {
      dispatch(setEnumData(enumResponse.data));
    }
    if (enumResponse.isError) {
      let errorData: any = enumResponse.error;
      let msg: any = errorData?.data?.serviceErrors[0]?.msg;
      toast.error('Something went wrong ' + msg || '');
    }
  }, [enumResponse.isSuccess, enumResponse.isSuccess]);

  let enumData: any = useAppSelector(getSpecificEnumData);

  useEffect(() => {
    if (enumData) {
      setOfferType(enumData['OFFER_TYPE']);
      setOfferFor(enumData['OFFER_FOR']);
    }
  }, [enumData]);

  const handleAddOrUpdateCity = (values: any, action: any) => {
    if (!state.editMode) {
      saveOfferDetail({
        token: token,
        payload: {
          cityId: parseInt(values.selectedCity),
          expiryDate: new Date(values.offerEndsAt).toISOString(),
          offerTitle: values.offerTitle,
          offerType: values.offerType,
          offerValue: parseInt(values.offerValue),
          promoCode: values.promoCode,
          startDate: new Date(values.offerStartsFrom).toISOString(),
          userCategory: 'UCNEW',
          status: true,
          validIn: values.offerFor,
        },
      });
    } else {
      updateOfferDetail({
        token: token,
        payload: {
          id: state.editData.id,
          cityId: parseInt(values.selectedCity),
          expiryDate: new Date(values.offerEndsAt).toISOString(),
          offerTitle: values.offerTitle,
          offerType: values.offerType,
          offerValue: parseInt(values.offerValue),
          promoCode: values.promoCode,
          startDate: new Date(values.offerStartsFrom).toISOString(),
          userCategory: 'UCNEW',
          status: true,
          validIn: values.offerFor,
        },
      });
    }
  };

  useEffect(() => {
    if (state.editData) {
      setInitialValues({
        offerFor: state.editData.validIn,
        offerTitle: state.editData.offerTitle,
        selectedCity: state.editData.cityId,
        offerType: state.editData.offerType,
        offerStartsFrom: state.editData.startDate,
        offerEndsAt: state.editData.expiryDate,
        offerValue: state.editData.offerValue,
        promoCode: state.editData.promoCode,
      });
    }
    getAllCities({
      token: token,
      payload: {
        offset: 100,
        page: 0,
        status: true,
      },
    });
  }, []);

  useEffect(() => {
    if (saveOfferDetailResponse.isSuccess) {
      getAllOfferDetail({
        token: token,
        payload: {
          page: page,
          orderBy: order,
          offset: rowsPerPage,
        },
      });
      toast.success('Offer added successfully');
      navigate('/dashboard/offers', { replace: true });
    } else if (saveOfferDetailResponse.isError) {
      let errorData: any = saveOfferDetailResponse.error;
      let msg: any = errorData?.data?.serviceErrors[0]?.msg;
      toast.error('Something went wrong ' + msg || '');
    }

    if (updateOfferDetailResponse.isSuccess) {
      toast.success('Offer updated successfully');
      navigate('/dashboard/offers', { replace: true });
    } else if (updateOfferDetailResponse.isError) {
      let errorData: any = updateOfferDetailResponse.error;
      let msg: any = errorData?.data?.serviceErrors[0]?.msg;
      toast.error('Something went wrong ' + msg || '');
    }
  }, [
    saveOfferDetailResponse.isSuccess,
    saveOfferDetailResponse.isError,
    updateOfferDetailResponse.isSuccess,
    updateOfferDetailResponse.isError,
  ]);

  useEffect(() => {
    if (getAllCitiesResponse.isSuccess) {
      const cities = getAllCitiesResponse.data.data.filter((item: any) => {
        return item.status == true;
      });
      setCityData(cities);
    } else if (getAllCitiesResponse.isError) {
      let errorData: any = getAllCitiesResponse.error;
      let msg: any = errorData?.data?.serviceErrors[0]?.msg;
      toast.error('Something went wrong ' + msg || '');
    }
  }, [getAllCitiesResponse.isSuccess, getAllCitiesResponse.isError]);

  return (
    <Box>
      <Container>
        <h1>{state.editMode ? 'Edit Offer' : 'Add Offer'}</h1>
        <ResponsiveBox style={{ display: 'flex', paddingTop: '40px' }}>
          <FormikBox style={{ flex: 1, padding: 0 }}>
            <Formik
              enableReinitialize
              initialValues={initialValues}
              onSubmit={handleAddOrUpdateCity}
              validationSchema={offerCode === 'OFCITY' ? OfferSchema : OfferTypeSchema}
            >
              {({ handleBlur, handleChange, handleSubmit, setFieldValue, values, touched, errors }) => (
                <Box style={{ width: '100%' }}>
                  <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {/* Offer For */}
                    <Box style={{ width: '100%', marginRight: '20px' }}>
                      <Typography variant="h5" marginTop={2} gutterBottom component="div">
                        Offer For
                      </Typography>
                      <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        style={{ width: '100%' }}
                        value={values.offerFor}
                        onChange={(event: any) => {
                          setOfferCode(event.target.value);
                          setFieldValue('offerFor', event.target.value);
                        }}
                        onBlur={handleBlur}
                        error={Boolean(touched.offerFor && errors.offerFor)}
                        displayEmpty
                        defaultValue={''}
                      >
                        <MenuItem disabled value="">
                          Select offer for
                        </MenuItem>
                        {offerFor &&
                          offerFor.map((offerFors: any) => (
                            <MenuItem key={offerFors.code} value={offerFors.code}>
                              {offerFors.displayValue}
                            </MenuItem>
                          ))}
                      </Select>
                    </Box>
                    {/* Offer Title */}
                    <Box style={{ width: '100%' }}>
                      <Typography variant="h5" marginTop={2} gutterBottom component="div">
                        Offer Title
                      </Typography>
                      <TextField
                        fullWidth
                        id="offerTitle"
                        autoComplete=" offerTitle"
                        type="text"
                        name="offerTitle"
                        label="Enter offer title"
                        value={values.offerTitle}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(touched.offerTitle && errors.offerTitle)}
                        helperText={touched.offerTitle && errors.offerTitle}
                      />
                    </Box>
                  </Box>
                  <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {/* Offer For City */}
                    {values.offerFor === 'OFCITY' && (
                      <Box style={{ width: '100%', marginRight: '20px' }}>
                        <Typography variant="h5" marginTop={2} gutterBottom component="div">
                          Select City
                        </Typography>
                        <Select
                          id="selectedCity"
                          name="selectedCity"
                          style={{ width: '100%' }}
                          value={values.selectedCity}
                          onChange={(event: any) => {
                            setFieldValue('selectedCity', event.target.value);
                          }}
                          onBlur={handleBlur}
                          error={Boolean(touched.selectedCity && errors.selectedCity)}
                          displayEmpty
                          defaultValue={''}
                        >
                          <MenuItem disabled value="">
                            Select city
                          </MenuItem>
                          {cityData &&
                            cityData.map((city: any) => (
                              <MenuItem key={city.id} value={city.id}>
                                {city.name}
                              </MenuItem>
                            ))}
                        </Select>
                      </Box>
                    )}
                    {/* Offer For Customer */}
                    <Box style={{ width: '100%' }}>
                      <Typography variant="h5" marginTop={2} gutterBottom component="div">
                        Offer Type
                      </Typography>
                      <Select
                        id="offerType"
                        name="offerType"
                        style={{ width: '100%' }}
                        value={values.offerType}
                        onChange={(event: any) => {
                          setFieldValue('offerType', event.target.value);
                        }}
                        onBlur={handleBlur}
                        error={Boolean(touched.offerType && errors.offerType)}
                        displayEmpty
                        defaultValue={''}
                      >
                        <MenuItem disabled value="">
                          Select offer type
                        </MenuItem>
                        {offerType &&
                          offerType.map((offerTypes: any) => (
                            <MenuItem key={offerTypes.code} value={offerTypes.code}>
                              {offerTypes.displayValue}
                            </MenuItem>
                          ))}
                      </Select>
                    </Box>
                  </Box>
                  <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box style={{ width: '100%', marginRight: '20px' }}>
                      <Typography variant="h5" marginTop={2} gutterBottom component="div">
                        Offer Starts From
                      </Typography>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="Enter offer starts from"
                          value={
                            state.editMode == true
                              ? values.offerStartsFrom
                              : moment(moment(values.offerStartsFrom, 'DD-MM-YYYY'), 'MM-DD-YYYY')
                          }
                          onChange={(value) => {
                            setFieldValue('offerStartsFrom', value);
                          }}
                          inputFormat="dd-MM-yyyy"
                          renderInput={(params) => (
                            <TextField
                              id="offerStartsFrom"
                              name="offerStartsFrom"
                              style={{ width: '100%' }}
                              onBlur={handleBlur}
                              onChange={(value: any) => {
                                setFieldValue('offerStartsFrom', value?.target.value);
                              }}
                              {...params}
                              error={Boolean(touched.offerStartsFrom && errors.offerStartsFrom)}
                              helperText={touched.offerStartsFrom && errors.offerStartsFrom}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </Box>

                    <Box style={{ width: '100%' }}>
                      <Typography variant="h5" marginTop={2} gutterBottom component="div">
                        Offer Ends At
                      </Typography>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="Enter offer ends at"
                          value={
                            state.editMode == true
                              ? values.offerEndsAt
                              : moment(moment(values.offerEndsAt, 'DD-MM-YYYY'), 'MM-DD-YYYY')
                          }
                          onChange={(value) => {
                            setFieldValue('offerEndsAt', value);
                          }}
                          inputFormat="dd-MM-yyyy"
                          renderInput={(params) => (
                            <TextField
                              id="offerEndsAt"
                              name="offerEndsAt"
                              style={{ width: '100%' }}
                              onBlur={handleBlur}
                              onChange={(value: any) => {
                                setFieldValue('offerEndsAt', value?.target.value);
                              }}
                              {...params}
                              error={Boolean(touched.offerEndsAt && errors.offerEndsAt)}
                              helperText={touched.offerEndsAt && errors.offerEndsAt}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </Box>
                  </Box>
                  <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box style={{ width: '100%', marginRight: '20px' }}>
                      <Typography variant="h5" marginTop={2} gutterBottom component="div">
                        Offer {values.offerType === 'OTPRCNT' ? '%' : 'Amount'}
                      </Typography>
                      <TextField
                        fullWidth
                        autoComplete=" offerValue"
                        type="number"
                        name="offerValue"
                        label="Enter offer"
                        value={values.offerValue}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(touched.offerValue && errors.offerValue)}
                        helperText={touched.offerValue && errors.offerValue}
                      />
                    </Box>

                    <Box style={{ width: '100%' }}>
                      <Typography variant="h5" marginTop={2} gutterBottom component="div">
                        Promo Code
                      </Typography>
                      <TextField
                        fullWidth
                        autoComplete="promoCode"
                        type="text"
                        name="promoCode"
                        label="Enter promo code"
                        value={values.promoCode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(touched.promoCode && errors.promoCode)}
                        helperText={touched.promoCode && errors.promoCode}
                      />
                    </Box>
                  </Box>

                  <Box style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                    <Button
                      style={{ color: 'white', backgroundColor: '#E23744' }}
                      variant="contained"
                      onClick={() => {
                        navigate('/dashboard/offers');
                      }}
                      sx={{ marginRight: '10px' }}
                      size="large"
                    >
                      Cancel
                    </Button>
                    <Button
                      style={{ color: 'white', backgroundColor: '#E23744' }}
                      variant="contained"
                      size="large"
                      component="span"
                      onClick={() => {
                        handleSubmit();
                      }}
                    >
                      Save
                    </Button>
                  </Box>
                </Box>
              )}
            </Formik>
          </FormikBox>
        </ResponsiveBox>
      </Container>
      {saveOfferDetailResponse.isLoading ||
      updateOfferDetailResponse.isLoading ||
      getAllCitiesResponse.isLoading ||
      getAllOfferDetailResponse.isLoading ||
      enumResponse.isLoading ? (
        <Loading />
      ) : null}
    </Box>
  );
};

export default AddOrUpdateOffers;
